import type * as Operations from './operations';
import type { DocumentNode } from 'graphql';
import gql from 'graphql-tag';
export const MoneyFragmentDoc = gql`
  fragment Money on MoneyV2 {
    amount
    currencyCode
  }
`;
export const ProductCardFragmentDoc = gql`
  fragment ProductCard on Product {
    availableForSale
    id
    title
    tags
    handle
    productType
    vendor
    metafields(
      identifiers: [
        { namespace: "custom", key: "product_card_pattern_url" }
        { namespace: "custom", key: "variantcolor" }
        { namespace: "custom", key: "multiple_variant_images_by_alt_text" }
        { namespace: "custom", key: "product_card_object_fit_cover" }
        { namespace: "wishlist", key: "wishlist_counter" }
        { namespace: "custom", key: "pre_order_day" }
      ]
    ) {
      key
      value
    }
    options {
      name
      values
    }
    priceRange {
      minVariantPrice {
        amount
        currencyCode
      }
    }
    compareAtPriceRange {
      minVariantPrice {
        amount
        currencyCode
      }
    }
    images(first: 100) {
      nodes {
        url(transform: { preferredContentType: WEBP, maxWidth: 1024 })
        altText
        width
        height
        id
        __typename
      }
    }
    media(first: 100) {
      nodes {
        __typename
        ... on Video {
          sources {
            url
          }
          alt
          id
        }
      }
    }
    variants(first: 100) {
      nodes {
        id
        title
        availableForSale
        quantityAvailable
        barcode
        sku
        price {
          ...Money
        }
        compareAtPrice {
          ...Money
        }
        image {
          url(transform: { preferredContentType: WEBP, maxWidth: 128 })
          altText
          width
          height
        }
        sellingPlanAllocations(first: 10) {
          edges {
            node {
              sellingPlan {
                id
                name
              }
              priceAdjustments {
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      }
    }
    sellingPlanGroups(first: 1) {
      edges {
        node {
          name
          sellingPlans(first: 10) {
            edges {
              node {
                id
                name
              }
            }
          }
        }
      }
    }
  }
`;
export const QuickAddToCartFragmentDoc = gql`
  fragment QuickAddToCart on Product {
    availableForSale
    id
    handle
    title
    tags
    productType
    vendor
    metafields(
      identifiers: [
        { namespace: "custom", key: "product_card_pattern_url" }
        { namespace: "custom", key: "variantcolor" }
        { namespace: "custom", key: "multiple_variant_images_by_alt_text" }
        { namespace: "custom", key: "product_card_object_fit_cover" }
        { namespace: "wishlist", key: "wishlist_counter" }
        { namespace: "custom", key: "pre_order_day" }
      ]
    ) {
      key
      value
    }
    options {
      name
      values
    }
    featuredImage {
      url(transform: { preferredContentType: WEBP, maxWidth: 128 })
      altText
      width
      height
    }
    images(first: 100) {
      nodes {
        url(transform: { preferredContentType: WEBP, maxWidth: 1024 })
        altText
        width
        height
        id
        __typename
      }
    }
    variants(first: 100) {
      nodes {
        id
        title
        availableForSale
        quantityAvailable
        barcode
        sku
        price {
          ...Money
        }
        compareAtPrice {
          ...Money
        }
        image {
          url(transform: { preferredContentType: WEBP, maxWidth: 128 })
          altText
          width
          height
        }
        sellingPlanAllocations(first: 10) {
          edges {
            node {
              sellingPlan {
                id
                name
              }
              priceAdjustments {
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      }
    }
    sellingPlanGroups(first: 1) {
      edges {
        node {
          name
          sellingPlans(first: 10) {
            edges {
              node {
                id
                name
              }
            }
          }
        }
      }
    }
  }
`;
export const FilterFragmentDoc = gql`
  fragment Filter on Filter {
    id
    label
    type
    values {
      id
      input
      label
      count
    }
  }
`;
export const CompleteCartFragmentDoc = gql`
  fragment CompleteCart on Cart {
    id
    checkoutUrl
    totalQuantity
    discountCodes {
      applicable
      code
    }
    discountAllocations {
      __typename
      discountedAmount {
        ...Money
      }
      ... on CartCodeDiscountAllocation {
        code
      }
      ... on CartCustomDiscountAllocation {
        title
      }
    }
    cost {
      totalAmount {
        ...Money
      }
      totalAmountEstimated
      subtotalAmount {
        ...Money
      }
      subtotalAmountEstimated
      totalTaxAmount {
        ...Money
      }
      totalTaxAmountEstimated
      totalDutyAmount {
        ...Money
      }
      totalDutyAmountEstimated
    }
    lines(first: 250, reverse: true) {
      nodes {
        attributes {
          key
          value
        }
        discountAllocations {
          __typename
          discountedAmount {
            ...Money
          }
          ... on CartCustomDiscountAllocation {
            title
          }
        }
        id
        quantity
        cost {
          totalAmount {
            ...Money
          }
          amountPerQuantity {
            ...Money
          }
        }
        merchandise {
          ... on ProductVariant {
            id
            sku
            compareAtPrice {
              ...Money
            }
            price {
              ...Money
            }
            image {
              url(transform: { preferredContentType: WEBP, maxWidth: 192 })
              altText
            }
            selectedOptions {
              name
              value
            }
            product {
              availableForSale
              id
              handle
              title
              tags
              productType
              vendor
              metafields(
                identifiers: [
                  { namespace: "custom", key: "product_card_pattern_url" }
                  { namespace: "custom", key: "variantcolor" }
                  { namespace: "custom", key: "multiple_variant_images_by_alt_text" }
                  { namespace: "wishlist", key: "wishlist_counter" }
                  { namespace: "custom", key: "pre_order_day" }
                ]
              ) {
                key
                value
              }
              options {
                name
                values
              }
              images(first: 100) {
                nodes {
                  url(transform: { preferredContentType: WEBP, maxWidth: 1024 })
                  altText
                  width
                  height
                  id
                  __typename
                }
              }
              variants(first: 100) {
                nodes {
                  id
                  title
                  availableForSale
                  currentlyNotInStock
                  quantityAvailable
                  sku
                  price {
                    ...Money
                  }
                  compareAtPrice {
                    ...Money
                  }
                  sellingPlanAllocations(first: 10) {
                    edges {
                      node {
                        sellingPlan {
                          id
                          name
                        }
                        priceAdjustments {
                          price {
                            amount
                            currencyCode
                          }
                          compareAtPrice {
                            amount
                            currencyCode
                          }
                        }
                      }
                    }
                  }
                }
              }
              sellingPlanGroups(first: 1) {
                edges {
                  node {
                    name
                    sellingPlans(first: 10) {
                      edges {
                        node {
                          id
                          name
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        sellingPlanAllocation {
          sellingPlan {
            id
            name
          }
          priceAdjustments {
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            perDeliveryPrice {
              amount
              currencyCode
            }
          }
        }
      }
    }
  }
`;
export const GetAutomaticRecommendationsDocument = gql`
  query getAutomaticRecommendations(
    $productId: ID!
    $intent: ProductRecommendationIntent
    $lang: LanguageCode
    $country: CountryCode
  ) @inContext(language: $lang, country: $country) {
    productRecommendations(productId: $productId, intent: $intent) {
      ...ProductCard
    }
  }
  ${ProductCardFragmentDoc}
  ${MoneyFragmentDoc}
`;
export const GetProductCardsDocument = gql`
  query getProductCards($ids: [ID!]!, $lang: LanguageCode, $country: CountryCode)
  @inContext(language: $lang, country: $country) {
    nodes(ids: $ids) {
      __typename
      ... on Product {
        ...ProductCard
      }
    }
  }
  ${ProductCardFragmentDoc}
  ${MoneyFragmentDoc}
`;
export const GetProductIdDocument = gql`
  query getProductId($handle: String!) {
    product(handle: $handle) {
      id
      title
    }
  }
`;
export const GetCartRecommendationsDocument = gql`
  query getCartRecommendations($productId: ID!, $lang: LanguageCode, $country: CountryCode)
  @inContext(language: $lang, country: $country) {
    productRecommendations(productId: $productId) {
      ...ProductCard
      ...QuickAddToCart
    }
  }
  ${ProductCardFragmentDoc}
  ${MoneyFragmentDoc}
  ${QuickAddToCartFragmentDoc}
`;
export const GetMetaObjectsbyIdsDocument = gql`
  query getMetaObjectsbyIds($ids: [ID!]!) {
    nodes(ids: $ids) {
      __typename
      ... on Metaobject {
        id
        fields {
          key
          value
        }
      }
    }
  }
`;
export const GetProductVariantsByIdsDocument = gql`
  query getProductVariantsByIds($ids: [ID!]!) {
    nodes(ids: $ids) {
      __typename
      ... on ProductVariant {
        id
        title
      }
    }
  }
`;
export const GetCollectionDocument = gql`
  query getCollection(
    $handle: String!
    $filters: [ProductFilter!]
    $lang: LanguageCode
    $sortKey: ProductCollectionSortKeys
    $reverse: Boolean
    $pageSize: Int!
    $after: String
    $country: CountryCode
  ) @inContext(language: $lang, country: $country) {
    collection(handle: $handle) {
      title
      products(
        first: $pageSize
        filters: $filters
        after: $after
        sortKey: $sortKey
        reverse: $reverse
      ) {
        nodes {
          ...ProductCard
        }
        pageInfo {
          hasNextPage
          endCursor
        }
        filters {
          ...Filter
        }
      }
    }
  }
  ${ProductCardFragmentDoc}
  ${MoneyFragmentDoc}
  ${FilterFragmentDoc}
`;
export const GetCurrencyCodeDocument = gql`
  query getCurrencyCode {
    products(first: 1) {
      nodes {
        priceRange {
          minVariantPrice {
            currencyCode
          }
        }
      }
    }
  }
`;
export const GetSearchResultsDocument = gql`
  query getSearchResults(
    $query: String!
    $filters: [ProductFilter!]
    $pageSize: Int!
    $after: String
    $reverse: Boolean
    $sortKey: SearchSortKeys
    $lang: LanguageCode
    $country: CountryCode
  ) @inContext(language: $lang, country: $country) {
    search(
      query: $query
      first: $pageSize
      after: $after
      productFilters: $filters
      reverse: $reverse
      sortKey: $sortKey
      types: PRODUCT
    ) {
      edges {
        node {
          __typename
          ... on Product {
            ...ProductCard
          }
        }
      }
      productFilters {
        ...Filter
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${ProductCardFragmentDoc}
  ${MoneyFragmentDoc}
  ${FilterFragmentDoc}
`;
export const CustomerResetByUrlDocument = gql`
  mutation customerResetByUrl($resetUrl: URL!, $password: String!) {
    customerResetByUrl(resetUrl: $resetUrl, password: $password) {
      customerUserErrors {
        code
        field
        message
      }
    }
  }
`;
export const CustomerAccessTokenCreateDocument = gql`
  mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
    customerAccessTokenCreate(input: $input) {
      customerAccessToken {
        accessToken
        expiresAt
      }
      customerUserErrors {
        code
        field
        message
      }
    }
  }
`;
export const CustomerRecoverDocument = gql`
  mutation customerRecover($email: String!) {
    customerRecover(email: $email) {
      customerUserErrors {
        code
        field
        message
      }
    }
  }
`;
export const CustomerCreateDocument = gql`
  mutation customerCreate($input: CustomerCreateInput!) {
    customerCreate(input: $input) {
      customerUserErrors {
        code
        field
        message
      }
    }
  }
`;
export const GetPredictiveSearchDocument = gql`
  query getPredictiveSearch($query: String!, $lang: LanguageCode, $country: CountryCode)
  @inContext(language: $lang, country: $country) {
    predictiveSearch(
      query: $query
      limit: 5
      limitScope: EACH
      searchableFields: [PRODUCT_TYPE, TAG, TITLE, VARIANTS_TITLE]
    ) {
      queries {
        text
      }
      products {
        ...ProductCard
      }
    }
  }
  ${ProductCardFragmentDoc}
  ${MoneyFragmentDoc}
`;
export const GetOrderByIdDocument = gql`
  query getOrderById($id: ID!) {
    nodes(ids: [$id]) {
      ... on Order {
        __typename
        id
        statusUrl
        orderNumber
        processedAt
        financialStatus
        totalPrice {
          ...Money
        }
        shippingAddress {
          formatted
        }
        billingAddress {
          formatted
        }
        fulfillmentStatus
        name
        successfulFulfillments {
          trackingInfo {
            number
            url
          }
          trackingCompany
          fulfillmentLineItems(first: 250, reverse: true) {
            nodes {
              lineItem {
                title
                quantity
              }
            }
          }
        }
        lineItems(first: 250, reverse: true) {
          nodes {
            quantity
            title
            variant {
              image {
                url(transform: { preferredContentType: WEBP, maxWidth: 128 })
                altText
                width
                height
              }
              selectedOptions {
                name
                value
              }
              product {
                ...QuickAddToCart
              }
            }
          }
        }
      }
    }
  }
  ${MoneyFragmentDoc}
  ${QuickAddToCartFragmentDoc}
`;
export const GetQuickAddToCartProductDocument = gql`
  query getQuickAddToCartProduct($handle: String!, $country: CountryCode, $lang: LanguageCode)
  @inContext(language: $lang, country: $country) {
    product(handle: $handle) {
      ...ProductCard
      ...QuickAddToCart
    }
  }
  ${ProductCardFragmentDoc}
  ${MoneyFragmentDoc}
  ${QuickAddToCartFragmentDoc}
`;
export const GetProductCardDocument = gql`
  query getProductCard($handle: String!, $lang: LanguageCode, $country: CountryCode)
  @inContext(language: $lang, country: $country) {
    product(handle: $handle) {
      ...ProductCard
    }
  }
  ${ProductCardFragmentDoc}
  ${MoneyFragmentDoc}
`;
export const GetWishlistCounterDocument = gql`
  query getWishlistCounter($id: ID) {
    product(id: $id) {
      id
      metafields(identifiers: [{ namespace: "wishlist", key: "wishlist_counter" }]) {
        id
        key
        value
      }
    }
  }
`;
export const GetRegionWishlistCounterDocument = gql`
  query getRegionWishlistCounter($id: ID!, $value: String!) {
    product(id: $id) {
      id
      metafields(identifiers: [{ namespace: "custom", key: $value }]) {
        id
        key
        value
      }
    }
  }
`;
export const GetCollectionSeoDocument = gql`
  query getCollectionSeo($handle: String, $lang: LanguageCode) @inContext(language: $lang) {
    collection(handle: $handle) {
      title
      description
      seo {
        title
        description
      }
    }
  }
`;
export const GetCustomerMarketingDocument = gql`
  query getCustomerMarketing($accessToken: String!) {
    customer(customerAccessToken: $accessToken) {
      acceptsMarketing
    }
  }
`;
export const GetCustomerIdDocument = gql`
  query getCustomerId($accessToken: String!) {
    customer(customerAccessToken: $accessToken) {
      id
    }
  }
`;
export const GetCustomerOrdersDocument = gql`
  query getCustomerOrders($accessToken: String!, $query: String) {
    customer(customerAccessToken: $accessToken) {
      orders(first: 250, query: $query, reverse: true) {
        nodes {
          __typename
          id
          name
          orderNumber
          currencyCode
          totalPrice {
            ...Money
          }
          financialStatus
          processedAt
          fulfillmentStatus
          statusUrl
        }
      }
    }
  }
  ${MoneyFragmentDoc}
`;
export const CustomerUpdateDocument = gql`
  mutation customerUpdate($accessToken: String!, $customer: CustomerUpdateInput!) {
    customerUpdate(customerAccessToken: $accessToken, customer: $customer) {
      customerUserErrors {
        code
        field
        message
      }
    }
  }
`;
export const GetCustomerProfileDocument = gql`
  query getCustomerProfile($accessToken: String!) {
    customer(customerAccessToken: $accessToken) {
      firstName
      lastName
      email
      phone
    }
  }
`;
export const GetCustomerAddressesDocument = gql`
  query getCustomerAddresses($accessToken: String!) {
    customer(customerAccessToken: $accessToken) {
      defaultAddress {
        id
      }
      addresses(first: 100) {
        nodes {
          id
          address1
          city
          country
          zip
          formattedArea
        }
      }
    }
  }
`;
export const CustomerAddressUpdateDocument = gql`
  mutation customerAddressUpdate($accessToken: String!, $id: ID!, $address: MailingAddressInput!) {
    customerAddressUpdate(customerAccessToken: $accessToken, id: $id, address: $address) {
      customerAddress {
        id
      }
      customerUserErrors {
        code
        field
        message
      }
    }
  }
`;
export const CustomerDefaultAddressUpdateDocument = gql`
  mutation customerDefaultAddressUpdate($accessToken: String!, $id: ID!) {
    customerDefaultAddressUpdate(customerAccessToken: $accessToken, addressId: $id) {
      customerUserErrors {
        code
        field
        message
      }
    }
  }
`;
export const CustomerAddressCreateDocument = gql`
  mutation customerAddressCreate($accessToken: String!, $address: MailingAddressInput!) {
    customerAddressCreate(customerAccessToken: $accessToken, address: $address) {
      customerAddress {
        id
      }
      customerUserErrors {
        code
        field
        message
      }
    }
  }
`;
export const CustomerAddressDeleteDocument = gql`
  mutation customerAddressDelete($accessToken: String!, $id: ID!) {
    customerAddressDelete(customerAccessToken: $accessToken, id: $id) {
      customerUserErrors {
        code
        field
        message
      }
    }
  }
`;
export const CreateCartDocument = gql`
  mutation createCart($lines: [CartLineInput!]!, $buyerIdentity: CartBuyerIdentityInput) {
    cartCreate(input: { lines: $lines, buyerIdentity: $buyerIdentity }) {
      cart {
        ...CompleteCart
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CompleteCartFragmentDoc}
  ${MoneyFragmentDoc}
`;
export const AddToCartDocument = gql`
  mutation addToCart($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        ...CompleteCart
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CompleteCartFragmentDoc}
  ${MoneyFragmentDoc}
`;
export const UpdateCartQuantityDocument = gql`
  mutation updateCartQuantity($cartId: ID!, $lineId: ID!, $quantity: Int!) {
    cartLinesUpdate(cartId: $cartId, lines: [{ id: $lineId, quantity: $quantity }]) {
      cart {
        ...CompleteCart
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CompleteCartFragmentDoc}
  ${MoneyFragmentDoc}
`;
export const AddDiscountCodeDocument = gql`
  mutation addDiscountCode($cartId: ID!, $discountCodes: [String!]) {
    cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
      cart {
        ...CompleteCart
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CompleteCartFragmentDoc}
  ${MoneyFragmentDoc}
`;
export const GetCartDocument = gql`
  query getCart($id: ID!, $lang: LanguageCode, $country: CountryCode)
  @inContext(language: $lang, country: $country) {
    cart(id: $id) {
      ...CompleteCart
    }
  }
  ${CompleteCartFragmentDoc}
  ${MoneyFragmentDoc}
`;
export const GetCustomerForCheckoutDocument = gql`
  query getCustomerForCheckout($accessToken: String!) {
    customer(customerAccessToken: $accessToken) {
      email
    }
  }
`;
export const GetCustomerDocument = gql`
  query getCustomer($accessToken: String!) {
    customer(customerAccessToken: $accessToken) {
      id
      email
      firstName
      lastName
      displayName
      defaultAddress {
        id
        countryCodeV2
        city
        province
      }
      wishlist: metafield(key: "wishlist", namespace: "wishlist") {
        id
        value
      }
      tags
    }
  }
`;
export const CustomerAccessTokenDeleteDocument = gql`
  mutation customerAccessTokenDelete($accessToken: String!) {
    customerAccessTokenDelete(customerAccessToken: $accessToken) {
      deletedAccessToken
    }
  }
`;
export const UpdateCartBuyerIdentityDocument = gql`
  mutation updateCartBuyerIdentity($buyerIdentity: CartBuyerIdentityInput!, $cartId: ID!) {
    cartBuyerIdentityUpdate(buyerIdentity: $buyerIdentity, cartId: $cartId) {
      cart {
        id
      }
      userErrors {
        field
        message
      }
    }
  }
`;
export const GetWishlistProductsDocument = gql`
  query getWishlistProducts($ids: [ID!]!, $lang: LanguageCode, $country: CountryCode)
  @inContext(language: $lang, country: $country) {
    nodes(ids: $ids) {
      __typename
      ... on Product {
        ...ProductCard
        ...QuickAddToCart
      }
    }
  }
  ${ProductCardFragmentDoc}
  ${MoneyFragmentDoc}
  ${QuickAddToCartFragmentDoc}
`;
export const GetProductDocument = gql`
  query getProduct($handle: String!, $language: LanguageCode) @inContext(language: $language) {
    product(handle: $handle) {
      handle
      id
      title
      description
      seo {
        title
        description
      }
      productType
      handle
      availableForSale
      vendor
      collections(first: 100) {
        nodes {
          title
        }
      }
      options {
        name
        values
      }
      images(first: 100) {
        nodes {
          url(transform: { preferredContentType: WEBP, maxWidth: 1024 })
          altText
          width
          height
          id
          __typename
        }
      }
      variants(first: 100) {
        nodes {
          id
          title
          availableForSale
          currentlyNotInStock
          quantityAvailable
          sku
          barcode
          price {
            ...Money
          }
          compareAtPrice {
            ...Money
          }
          sellingPlanAllocations(first: 10) {
            edges {
              node {
                sellingPlan {
                  id
                  name
                }
                priceAdjustments {
                  price {
                    amount
                    currencyCode
                  }
                  compareAtPrice {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
      }
      priceRange {
        minVariantPrice {
          ...Money
        }
      }
      metafields(
        identifiers: [
          { namespace: "custom", key: "variantcolor" }
          { namespace: "custom", key: "save_on_sets" }
          { namespace: "custom", key: "multiple_variant_images_by_alt_text" }
          { namespace: "custom", key: "wishlist_counter_eu" }
          { namespace: "custom", key: "wishlist_counter_us" }
          { namespace: "custom", key: "wishlist_counter_uk" }
          { namespace: "custom", key: "a_test_version_media_index" }
          { namespace: "wishlist", key: "wishlist_counter" }
          { namespace: "custom", key: "pre_order_day" }
        ]
      ) {
        id
        key
        value
      }
      seo {
        title
        description
      }
      totalInventory
      tags
      media(first: 100) {
        nodes {
          previewImage {
            url(transform: { preferredContentType: WEBP, maxWidth: 1024 })
            id
          }
          __typename
          ... on Video {
            sources {
              url
            }
            alt
            id
          }
        }
      }
      sellingPlanGroups(first: 1) {
        edges {
          node {
            name
            sellingPlans(first: 10) {
              edges {
                node {
                  id
                  name
                }
              }
            }
          }
        }
      }
    }
  }
  ${MoneyFragmentDoc}
`;
export const GetRegionalizedProductPriceDocument = gql`
  query getRegionalizedProductPrice($handle: String!, $lang: LanguageCode, $country: CountryCode)
  @inContext(language: $lang, country: $country) {
    product(handle: $handle) {
      variants(first: 100) {
        nodes {
          id
          title
          availableForSale
          currentlyNotInStock
          quantityAvailable
          sku
          price {
            ...Money
          }
          compareAtPrice {
            ...Money
          }
          sellingPlanAllocations(first: 10) {
            edges {
              node {
                sellingPlan {
                  id
                  name
                }
                priceAdjustments {
                  price {
                    amount
                    currencyCode
                  }
                  compareAtPrice {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
      }
      priceRange {
        minVariantPrice {
          ...Money
        }
      }
    }
  }
  ${MoneyFragmentDoc}
`;
export const GetRegionalizedProductPriceByIdDocument = gql`
  query getRegionalizedProductPriceById($id: ID!, $lang: LanguageCode, $country: CountryCode)
  @inContext(language: $lang, country: $country) {
    product(id: $id) {
      id
      title
      handle
      productType
      variants(first: 100) {
        nodes {
          id
          title
          availableForSale
          currentlyNotInStock
          quantityAvailable
          sku
          price {
            ...Money
          }
          compareAtPrice {
            ...Money
          }
          sellingPlanAllocations(first: 10) {
            edges {
              node {
                sellingPlan {
                  id
                  name
                }
                priceAdjustments {
                  price {
                    amount
                    currencyCode
                  }
                  compareAtPrice {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
      }
      priceRange {
        minVariantPrice {
          ...Money
        }
      }
    }
  }
  ${MoneyFragmentDoc}
`;
export const LocalizationDocument = gql`
  query Localization @inContext(language: EN) {
    localization {
      availableLanguages {
        isoCode
        endonymName
      }
    }
  }
`;
export type Requester<C = {}, E = unknown> = <R, V>(
  doc: DocumentNode,
  vars?: V,
  options?: C
) => Promise<R> | AsyncIterable<R>;
export function getSdk<C, E>(requester: Requester<C, E>) {
  return {
    getAutomaticRecommendations(
      variables: Operations.GetAutomaticRecommendationsQueryVariables,
      options?: C
    ): Promise<Operations.GetAutomaticRecommendationsQuery> {
      return requester<
        Operations.GetAutomaticRecommendationsQuery,
        Operations.GetAutomaticRecommendationsQueryVariables
      >(
        GetAutomaticRecommendationsDocument,
        variables,
        options
      ) as Promise<Operations.GetAutomaticRecommendationsQuery>;
    },
    getProductCards(
      variables: Operations.GetProductCardsQueryVariables,
      options?: C
    ): Promise<Operations.GetProductCardsQuery> {
      return requester<Operations.GetProductCardsQuery, Operations.GetProductCardsQueryVariables>(
        GetProductCardsDocument,
        variables,
        options
      ) as Promise<Operations.GetProductCardsQuery>;
    },
    getProductId(
      variables: Operations.GetProductIdQueryVariables,
      options?: C
    ): Promise<Operations.GetProductIdQuery> {
      return requester<Operations.GetProductIdQuery, Operations.GetProductIdQueryVariables>(
        GetProductIdDocument,
        variables,
        options
      ) as Promise<Operations.GetProductIdQuery>;
    },
    getCartRecommendations(
      variables: Operations.GetCartRecommendationsQueryVariables,
      options?: C
    ): Promise<Operations.GetCartRecommendationsQuery> {
      return requester<
        Operations.GetCartRecommendationsQuery,
        Operations.GetCartRecommendationsQueryVariables
      >(
        GetCartRecommendationsDocument,
        variables,
        options
      ) as Promise<Operations.GetCartRecommendationsQuery>;
    },
    getMetaObjectsbyIds(
      variables: Operations.GetMetaObjectsbyIdsQueryVariables,
      options?: C
    ): Promise<Operations.GetMetaObjectsbyIdsQuery> {
      return requester<
        Operations.GetMetaObjectsbyIdsQuery,
        Operations.GetMetaObjectsbyIdsQueryVariables
      >(
        GetMetaObjectsbyIdsDocument,
        variables,
        options
      ) as Promise<Operations.GetMetaObjectsbyIdsQuery>;
    },
    getProductVariantsByIds(
      variables: Operations.GetProductVariantsByIdsQueryVariables,
      options?: C
    ): Promise<Operations.GetProductVariantsByIdsQuery> {
      return requester<
        Operations.GetProductVariantsByIdsQuery,
        Operations.GetProductVariantsByIdsQueryVariables
      >(
        GetProductVariantsByIdsDocument,
        variables,
        options
      ) as Promise<Operations.GetProductVariantsByIdsQuery>;
    },
    getCollection(
      variables: Operations.GetCollectionQueryVariables,
      options?: C
    ): Promise<Operations.GetCollectionQuery> {
      return requester<Operations.GetCollectionQuery, Operations.GetCollectionQueryVariables>(
        GetCollectionDocument,
        variables,
        options
      ) as Promise<Operations.GetCollectionQuery>;
    },
    getCurrencyCode(
      variables?: Operations.GetCurrencyCodeQueryVariables,
      options?: C
    ): Promise<Operations.GetCurrencyCodeQuery> {
      return requester<Operations.GetCurrencyCodeQuery, Operations.GetCurrencyCodeQueryVariables>(
        GetCurrencyCodeDocument,
        variables,
        options
      ) as Promise<Operations.GetCurrencyCodeQuery>;
    },
    getSearchResults(
      variables: Operations.GetSearchResultsQueryVariables,
      options?: C
    ): Promise<Operations.GetSearchResultsQuery> {
      return requester<Operations.GetSearchResultsQuery, Operations.GetSearchResultsQueryVariables>(
        GetSearchResultsDocument,
        variables,
        options
      ) as Promise<Operations.GetSearchResultsQuery>;
    },
    customerResetByUrl(
      variables: Operations.CustomerResetByUrlMutationVariables,
      options?: C
    ): Promise<Operations.CustomerResetByUrlMutation> {
      return requester<
        Operations.CustomerResetByUrlMutation,
        Operations.CustomerResetByUrlMutationVariables
      >(
        CustomerResetByUrlDocument,
        variables,
        options
      ) as Promise<Operations.CustomerResetByUrlMutation>;
    },
    customerAccessTokenCreate(
      variables: Operations.CustomerAccessTokenCreateMutationVariables,
      options?: C
    ): Promise<Operations.CustomerAccessTokenCreateMutation> {
      return requester<
        Operations.CustomerAccessTokenCreateMutation,
        Operations.CustomerAccessTokenCreateMutationVariables
      >(
        CustomerAccessTokenCreateDocument,
        variables,
        options
      ) as Promise<Operations.CustomerAccessTokenCreateMutation>;
    },
    customerRecover(
      variables: Operations.CustomerRecoverMutationVariables,
      options?: C
    ): Promise<Operations.CustomerRecoverMutation> {
      return requester<
        Operations.CustomerRecoverMutation,
        Operations.CustomerRecoverMutationVariables
      >(CustomerRecoverDocument, variables, options) as Promise<Operations.CustomerRecoverMutation>;
    },
    customerCreate(
      variables: Operations.CustomerCreateMutationVariables,
      options?: C
    ): Promise<Operations.CustomerCreateMutation> {
      return requester<
        Operations.CustomerCreateMutation,
        Operations.CustomerCreateMutationVariables
      >(CustomerCreateDocument, variables, options) as Promise<Operations.CustomerCreateMutation>;
    },
    getPredictiveSearch(
      variables: Operations.GetPredictiveSearchQueryVariables,
      options?: C
    ): Promise<Operations.GetPredictiveSearchQuery> {
      return requester<
        Operations.GetPredictiveSearchQuery,
        Operations.GetPredictiveSearchQueryVariables
      >(
        GetPredictiveSearchDocument,
        variables,
        options
      ) as Promise<Operations.GetPredictiveSearchQuery>;
    },
    getOrderById(
      variables: Operations.GetOrderByIdQueryVariables,
      options?: C
    ): Promise<Operations.GetOrderByIdQuery> {
      return requester<Operations.GetOrderByIdQuery, Operations.GetOrderByIdQueryVariables>(
        GetOrderByIdDocument,
        variables,
        options
      ) as Promise<Operations.GetOrderByIdQuery>;
    },
    getQuickAddToCartProduct(
      variables: Operations.GetQuickAddToCartProductQueryVariables,
      options?: C
    ): Promise<Operations.GetQuickAddToCartProductQuery> {
      return requester<
        Operations.GetQuickAddToCartProductQuery,
        Operations.GetQuickAddToCartProductQueryVariables
      >(
        GetQuickAddToCartProductDocument,
        variables,
        options
      ) as Promise<Operations.GetQuickAddToCartProductQuery>;
    },
    getProductCard(
      variables: Operations.GetProductCardQueryVariables,
      options?: C
    ): Promise<Operations.GetProductCardQuery> {
      return requester<Operations.GetProductCardQuery, Operations.GetProductCardQueryVariables>(
        GetProductCardDocument,
        variables,
        options
      ) as Promise<Operations.GetProductCardQuery>;
    },
    getWishlistCounter(
      variables?: Operations.GetWishlistCounterQueryVariables,
      options?: C
    ): Promise<Operations.GetWishlistCounterQuery> {
      return requester<
        Operations.GetWishlistCounterQuery,
        Operations.GetWishlistCounterQueryVariables
      >(
        GetWishlistCounterDocument,
        variables,
        options
      ) as Promise<Operations.GetWishlistCounterQuery>;
    },
    getRegionWishlistCounter(
      variables: Operations.GetRegionWishlistCounterQueryVariables,
      options?: C
    ): Promise<Operations.GetRegionWishlistCounterQuery> {
      return requester<
        Operations.GetRegionWishlistCounterQuery,
        Operations.GetRegionWishlistCounterQueryVariables
      >(
        GetRegionWishlistCounterDocument,
        variables,
        options
      ) as Promise<Operations.GetRegionWishlistCounterQuery>;
    },
    getCollectionSeo(
      variables?: Operations.GetCollectionSeoQueryVariables,
      options?: C
    ): Promise<Operations.GetCollectionSeoQuery> {
      return requester<Operations.GetCollectionSeoQuery, Operations.GetCollectionSeoQueryVariables>(
        GetCollectionSeoDocument,
        variables,
        options
      ) as Promise<Operations.GetCollectionSeoQuery>;
    },
    getCustomerMarketing(
      variables: Operations.GetCustomerMarketingQueryVariables,
      options?: C
    ): Promise<Operations.GetCustomerMarketingQuery> {
      return requester<
        Operations.GetCustomerMarketingQuery,
        Operations.GetCustomerMarketingQueryVariables
      >(
        GetCustomerMarketingDocument,
        variables,
        options
      ) as Promise<Operations.GetCustomerMarketingQuery>;
    },
    getCustomerId(
      variables: Operations.GetCustomerIdQueryVariables,
      options?: C
    ): Promise<Operations.GetCustomerIdQuery> {
      return requester<Operations.GetCustomerIdQuery, Operations.GetCustomerIdQueryVariables>(
        GetCustomerIdDocument,
        variables,
        options
      ) as Promise<Operations.GetCustomerIdQuery>;
    },
    getCustomerOrders(
      variables: Operations.GetCustomerOrdersQueryVariables,
      options?: C
    ): Promise<Operations.GetCustomerOrdersQuery> {
      return requester<
        Operations.GetCustomerOrdersQuery,
        Operations.GetCustomerOrdersQueryVariables
      >(
        GetCustomerOrdersDocument,
        variables,
        options
      ) as Promise<Operations.GetCustomerOrdersQuery>;
    },
    customerUpdate(
      variables: Operations.CustomerUpdateMutationVariables,
      options?: C
    ): Promise<Operations.CustomerUpdateMutation> {
      return requester<
        Operations.CustomerUpdateMutation,
        Operations.CustomerUpdateMutationVariables
      >(CustomerUpdateDocument, variables, options) as Promise<Operations.CustomerUpdateMutation>;
    },
    getCustomerProfile(
      variables: Operations.GetCustomerProfileQueryVariables,
      options?: C
    ): Promise<Operations.GetCustomerProfileQuery> {
      return requester<
        Operations.GetCustomerProfileQuery,
        Operations.GetCustomerProfileQueryVariables
      >(
        GetCustomerProfileDocument,
        variables,
        options
      ) as Promise<Operations.GetCustomerProfileQuery>;
    },
    getCustomerAddresses(
      variables: Operations.GetCustomerAddressesQueryVariables,
      options?: C
    ): Promise<Operations.GetCustomerAddressesQuery> {
      return requester<
        Operations.GetCustomerAddressesQuery,
        Operations.GetCustomerAddressesQueryVariables
      >(
        GetCustomerAddressesDocument,
        variables,
        options
      ) as Promise<Operations.GetCustomerAddressesQuery>;
    },
    customerAddressUpdate(
      variables: Operations.CustomerAddressUpdateMutationVariables,
      options?: C
    ): Promise<Operations.CustomerAddressUpdateMutation> {
      return requester<
        Operations.CustomerAddressUpdateMutation,
        Operations.CustomerAddressUpdateMutationVariables
      >(
        CustomerAddressUpdateDocument,
        variables,
        options
      ) as Promise<Operations.CustomerAddressUpdateMutation>;
    },
    customerDefaultAddressUpdate(
      variables: Operations.CustomerDefaultAddressUpdateMutationVariables,
      options?: C
    ): Promise<Operations.CustomerDefaultAddressUpdateMutation> {
      return requester<
        Operations.CustomerDefaultAddressUpdateMutation,
        Operations.CustomerDefaultAddressUpdateMutationVariables
      >(
        CustomerDefaultAddressUpdateDocument,
        variables,
        options
      ) as Promise<Operations.CustomerDefaultAddressUpdateMutation>;
    },
    customerAddressCreate(
      variables: Operations.CustomerAddressCreateMutationVariables,
      options?: C
    ): Promise<Operations.CustomerAddressCreateMutation> {
      return requester<
        Operations.CustomerAddressCreateMutation,
        Operations.CustomerAddressCreateMutationVariables
      >(
        CustomerAddressCreateDocument,
        variables,
        options
      ) as Promise<Operations.CustomerAddressCreateMutation>;
    },
    customerAddressDelete(
      variables: Operations.CustomerAddressDeleteMutationVariables,
      options?: C
    ): Promise<Operations.CustomerAddressDeleteMutation> {
      return requester<
        Operations.CustomerAddressDeleteMutation,
        Operations.CustomerAddressDeleteMutationVariables
      >(
        CustomerAddressDeleteDocument,
        variables,
        options
      ) as Promise<Operations.CustomerAddressDeleteMutation>;
    },
    createCart(
      variables: Operations.CreateCartMutationVariables,
      options?: C
    ): Promise<Operations.CreateCartMutation> {
      return requester<Operations.CreateCartMutation, Operations.CreateCartMutationVariables>(
        CreateCartDocument,
        variables,
        options
      ) as Promise<Operations.CreateCartMutation>;
    },
    addToCart(
      variables: Operations.AddToCartMutationVariables,
      options?: C
    ): Promise<Operations.AddToCartMutation> {
      return requester<Operations.AddToCartMutation, Operations.AddToCartMutationVariables>(
        AddToCartDocument,
        variables,
        options
      ) as Promise<Operations.AddToCartMutation>;
    },
    updateCartQuantity(
      variables: Operations.UpdateCartQuantityMutationVariables,
      options?: C
    ): Promise<Operations.UpdateCartQuantityMutation> {
      return requester<
        Operations.UpdateCartQuantityMutation,
        Operations.UpdateCartQuantityMutationVariables
      >(
        UpdateCartQuantityDocument,
        variables,
        options
      ) as Promise<Operations.UpdateCartQuantityMutation>;
    },
    addDiscountCode(
      variables: Operations.AddDiscountCodeMutationVariables,
      options?: C
    ): Promise<Operations.AddDiscountCodeMutation> {
      return requester<
        Operations.AddDiscountCodeMutation,
        Operations.AddDiscountCodeMutationVariables
      >(AddDiscountCodeDocument, variables, options) as Promise<Operations.AddDiscountCodeMutation>;
    },
    getCart(
      variables: Operations.GetCartQueryVariables,
      options?: C
    ): Promise<Operations.GetCartQuery> {
      return requester<Operations.GetCartQuery, Operations.GetCartQueryVariables>(
        GetCartDocument,
        variables,
        options
      ) as Promise<Operations.GetCartQuery>;
    },
    getCustomerForCheckout(
      variables: Operations.GetCustomerForCheckoutQueryVariables,
      options?: C
    ): Promise<Operations.GetCustomerForCheckoutQuery> {
      return requester<
        Operations.GetCustomerForCheckoutQuery,
        Operations.GetCustomerForCheckoutQueryVariables
      >(
        GetCustomerForCheckoutDocument,
        variables,
        options
      ) as Promise<Operations.GetCustomerForCheckoutQuery>;
    },
    getCustomer(
      variables: Operations.GetCustomerQueryVariables,
      options?: C
    ): Promise<Operations.GetCustomerQuery> {
      return requester<Operations.GetCustomerQuery, Operations.GetCustomerQueryVariables>(
        GetCustomerDocument,
        variables,
        options
      ) as Promise<Operations.GetCustomerQuery>;
    },
    customerAccessTokenDelete(
      variables: Operations.CustomerAccessTokenDeleteMutationVariables,
      options?: C
    ): Promise<Operations.CustomerAccessTokenDeleteMutation> {
      return requester<
        Operations.CustomerAccessTokenDeleteMutation,
        Operations.CustomerAccessTokenDeleteMutationVariables
      >(
        CustomerAccessTokenDeleteDocument,
        variables,
        options
      ) as Promise<Operations.CustomerAccessTokenDeleteMutation>;
    },
    updateCartBuyerIdentity(
      variables: Operations.UpdateCartBuyerIdentityMutationVariables,
      options?: C
    ): Promise<Operations.UpdateCartBuyerIdentityMutation> {
      return requester<
        Operations.UpdateCartBuyerIdentityMutation,
        Operations.UpdateCartBuyerIdentityMutationVariables
      >(
        UpdateCartBuyerIdentityDocument,
        variables,
        options
      ) as Promise<Operations.UpdateCartBuyerIdentityMutation>;
    },
    getWishlistProducts(
      variables: Operations.GetWishlistProductsQueryVariables,
      options?: C
    ): Promise<Operations.GetWishlistProductsQuery> {
      return requester<
        Operations.GetWishlistProductsQuery,
        Operations.GetWishlistProductsQueryVariables
      >(
        GetWishlistProductsDocument,
        variables,
        options
      ) as Promise<Operations.GetWishlistProductsQuery>;
    },
    getProduct(
      variables: Operations.GetProductQueryVariables,
      options?: C
    ): Promise<Operations.GetProductQuery> {
      return requester<Operations.GetProductQuery, Operations.GetProductQueryVariables>(
        GetProductDocument,
        variables,
        options
      ) as Promise<Operations.GetProductQuery>;
    },
    getRegionalizedProductPrice(
      variables: Operations.GetRegionalizedProductPriceQueryVariables,
      options?: C
    ): Promise<Operations.GetRegionalizedProductPriceQuery> {
      return requester<
        Operations.GetRegionalizedProductPriceQuery,
        Operations.GetRegionalizedProductPriceQueryVariables
      >(
        GetRegionalizedProductPriceDocument,
        variables,
        options
      ) as Promise<Operations.GetRegionalizedProductPriceQuery>;
    },
    getRegionalizedProductPriceById(
      variables: Operations.GetRegionalizedProductPriceByIdQueryVariables,
      options?: C
    ): Promise<Operations.GetRegionalizedProductPriceByIdQuery> {
      return requester<
        Operations.GetRegionalizedProductPriceByIdQuery,
        Operations.GetRegionalizedProductPriceByIdQueryVariables
      >(
        GetRegionalizedProductPriceByIdDocument,
        variables,
        options
      ) as Promise<Operations.GetRegionalizedProductPriceByIdQuery>;
    },
    Localization(
      variables?: Operations.LocalizationQueryVariables,
      options?: C
    ): Promise<Operations.LocalizationQuery> {
      return requester<Operations.LocalizationQuery, Operations.LocalizationQueryVariables>(
        LocalizationDocument,
        variables,
        options
      ) as Promise<Operations.LocalizationQuery>;
    }
  };
}
export type Sdk = ReturnType<typeof getSdk>;
